import React, { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import SubscriptionTemplate from './SubscriptionTemplate';
import { displayMessage } from './wsHelpers';
import { connect } from 'react-redux';
import {
	updateAgendaItem,
	updateAttendeeAgendaItem,
	duplicateAttendeeAgendaItem,
	createAgendaItem,
	deleteAgendaItem,
	deleteAttendeeAgendaItem,
	thunkFetchAgenda,
	thunkFetchGroupAgenda,
	thunkFetchAttendeeAgenda,
	clearAgenda,
} from '@/redux/actions';

const WS_AGENDA_BY_HTTP = {
	CREATE: 'agenda/POST',
	UPDATE: 'agenda/:agendaItemID/PATCH',
	DELETE: 'agenda/:agendaItemID/DELETE',
};

const AgendaSubscriptions = props => {
	const {
		socket,
		currentEventId,
		children,
		updateAgendaItem,
		updateAttendeeAgenda,
		duplicateAttendeeAgendaItem,
		deleteAttendeeAgendaItem,
		createAgendaItem,
		deleteAgendaItem,
		fetchAgenda,
		clearAgenda,
		fetchGroupAgenda,
		fetchAttendeeAgenda,
		selectedPersonOrTrack,
		selectedView,
	} = props;

	useEffect(() => {
		return () => clearAgenda('AgendaSubscriptions.unmount');
	}, []);

	const subscribeToAgenda = () => {
		socket.emit('event/:eventID/agenda/SUBSCRIBE', { eventID: currentEventId });
	};

	const unsubscribeToAgenda = () => {
		socket.emit('event/:eventID/agenda/UNSUBSCRIBE', { eventID: currentEventId });
	};

	const refetchAgenda = from => {
		if (!selectedPersonOrTrack) {
			fetchAgenda(currentEventId, from);
		} else if (selectedView === 'track') {
			fetchGroupAgenda(currentEventId, selectedPersonOrTrack, from);
		} else {
			fetchAttendeeAgenda(selectedPersonOrTrack, currentEventId, from);
		}
	};

	const handleReconnect = () => refetchAgenda('AgendaSubscriptions.handleReconnect');

	// the create payload predates group attachment, so the count is only right after a refetch
	const refetchRef = useRef();
	refetchRef.current = () => refetchAgenda('AgendaSubscriptions.agendaItemCreated');
	const refetchAfterCreate = useMemo(() => debounce(() => refetchRef.current(), 500), []);
	useEffect(() => () => refetchAfterCreate.cancel(), [refetchAfterCreate]);

	const listenForCreatedAgendaItem = () =>
		socket.on(WS_AGENDA_BY_HTTP.CREATE, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Agenda Item could not be created', error);
			else {
				const { route } = payload;
				if (route === 'attendee') duplicateAttendeeAgendaItem(data);
				else createAgendaItem(data);
				refetchAfterCreate();
				displayMessage('Agenda Item created');
			}
		});

	const listenForUpdatedAgendaItem = () =>
		socket.on(WS_AGENDA_BY_HTTP.UPDATE, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error updating agenda item', error);
			else {
				const { name, id } = data;
				updateAttendeeAgenda(data);
				updateAgendaItem(data);
				displayMessage(`${name} (ID: ${id}) session updated`);
			}
		});

	const listenForDeletedAgendaItem = () =>
		socket.on(WS_AGENDA_BY_HTTP.DELETE, payload => {
			const { data, error } = payload;
			if (error) displayMessage('Error deleting agenda item', error);
			else {
				const { agendaItemName } = payload;
				deleteAttendeeAgendaItem(data);
				deleteAgendaItem(data);
				displayMessage(`${agendaItemName} session deleted`);
			}
		});

	const listeners = () => [
		listenForCreatedAgendaItem,
		listenForUpdatedAgendaItem,
		listenForDeletedAgendaItem,
	];

	return (
		<>
			<SubscriptionTemplate
				{...props}
				sub={subscribeToAgenda}
				unsub={unsubscribeToAgenda}
				listeners={listeners()}
				routes={Object.values(WS_AGENDA_BY_HTTP)}
				onReconnect={handleReconnect}
				from="AgendaSubscriptions"
			/>
			{children}
		</>
	);
};

const msp = ({ ws, auth, events, agenda }) => ({
	socket: ws.socket,
	auth0Token: auth.auth0Token,
	currentEventId: events.currentEvent.id,
	selectedPersonOrTrack: agenda.selectedPersonOrTrack,
	selectedView: agenda.selectedView,
});

const mdp = dispatch => ({
	updateAttendeeAgenda: agendaItem => dispatch(updateAttendeeAgendaItem(agendaItem)),
	updateAgendaItem: agendaItem => dispatch(updateAgendaItem(agendaItem)),
	duplicateAttendeeAgendaItem: agendaItem => dispatch(duplicateAttendeeAgendaItem(agendaItem)),
	createAgendaItem: agendaItem => dispatch(createAgendaItem(agendaItem)),
	deleteAgendaItem: agendaItem => dispatch(deleteAgendaItem(agendaItem)),
	deleteAttendeeAgendaItem: agendaItem => dispatch(deleteAttendeeAgendaItem(agendaItem)),
	fetchAgenda: (eventID, from) => dispatch(thunkFetchAgenda(eventID, from)),
	fetchGroupAgenda: (eventID, groupID, from) =>
		dispatch(thunkFetchGroupAgenda(eventID, groupID, from)),
	fetchAttendeeAgenda: (attendeeID, eventID, from) =>
		dispatch(thunkFetchAttendeeAgenda(attendeeID, eventID, from)),
	clearAgenda: from => dispatch(clearAgenda(from)),
});

export default connect(msp, mdp)(AgendaSubscriptions);
